// getHomeLiveStreamIds.js
'use strict';
import createCachedSelector from '../resource/createCachedSelector.js';

import getListData from '../selector/getListData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getRouterData from '../selector/getRouterData.js';
import getSelectedFeedQueryOptions from '../selector/getSelectedFeedQueryOptions.js';

import {
  SHOW_LIVE_STREAM_AD_THRESHOLD,
  livestreamAdType as livestreamAdTypeConstant,
} from '../resource/liveStreamConstants.js';
import { getIsChinaPartner } from '../resource/partner.js';
import {
  FEATURE_LIVESTREAM_AD,
  FEATURE_LIVESTREAM_AD_2,
  FEATURE_LIVESTREAM_FEED_DEDUPE,
  LIVESTREAM_FEED,
} from '../RemoteConfigKeys.js';

const defaultList = [];
const isChinaPartner = getIsChinaPartner();

/**
 * Select home live stream ids
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {array} selectPath - select path.
 * @param {number} topFeedCount - number of top feed items to exclude.
 * @return {array} The selected home live stream ids.
 */
const getHomeLiveStreamIds = createCachedSelector(
  (state, selectPath) => getListData(state, selectPath, 'itemIds'),
  state => getRemoteConfigData(state, FEATURE_LIVESTREAM_AD),
  state => getRemoteConfigData(state, FEATURE_LIVESTREAM_AD_2),
  state => getRemoteConfigData(state, FEATURE_LIVESTREAM_FEED_DEDUPE) === 1,
  (state, selectPath, topFeedCount) => topFeedCount,
  state =>
    getSelectedFeedQueryOptions(state, LIVESTREAM_FEED, undefined)?.length > 0,
  (state, selectPath) => getListData(state, selectPath, 'nextPage'),
  state => getRouterData(state, ['location', 'pathname']) === '/',
  (
    streamIds = defaultList,
    swaggerAdUrl = null,
    advertiserAdUrl = null,
    isLivestreamFeedDeduped = false,
    topFeedCount = 0,
    isSelectedFeedQueryOptions = false,
    nextPage = null,
    isAtNeedDedupePath = false
  ) => {
    const isLastPage = nextPage === null;

    // Top feed feature only works when no selected feed query options and on home page
    const shouldApplyTopFeedDeduped =
      isLivestreamFeedDeduped &&
      topFeedCount > 0 &&
      !isSelectedFeedQueryOptions &&
      isAtNeedDedupePath;

    let filteredIds;
    if (shouldApplyTopFeedDeduped) {
      if (isLastPage) {
        // Last page: move top feed items to the end
        const topFeedIds = streamIds.slice(0, topFeedCount);
        const remainingIds = streamIds.slice(topFeedCount);
        filteredIds = [...remainingIds, ...topFeedIds];
      } else {
        // Not last page: exclude top feed items
        filteredIds = streamIds.slice(topFeedCount);
      }
    } else {
      filteredIds = streamIds;
    }

    if (isChinaPartner) return filteredIds;

    let adArray = [];
    if (swaggerAdUrl) adArray.push(livestreamAdTypeConstant.SWAGGER);
    if (advertiserAdUrl) adArray.push(livestreamAdTypeConstant.ADVERTISER);

    if (filteredIds.length >= SHOW_LIVE_STREAM_AD_THRESHOLD) {
      const ids = [...filteredIds];
      ids.splice(SHOW_LIVE_STREAM_AD_THRESHOLD, 0, ...adArray);
      return ids;
    }

    return filteredIds;
  }
)(
  (state, selectPath, topFeedCount) =>
    `${selectPath.join(':')}:topFeed:${topFeedCount ?? 0}`
);

export default getHomeLiveStreamIds;
